import type { MesaOcupacao } from '../services/api.ts';
import type { ReservedTableMeta } from '../components/admin/RoomMaps.tsx';
import { extractReservationTableIds, formatPeopleLabel, getPeopleCount } from './reservationPeople.ts';

export const STATUS_LABELS: Record<string, string> = {
  pendente: 'Pendente',
  confirmada: 'Confirmada',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
  no_show: 'No-show',
};

export type CapacityWarningInfo = {
  title: string;
  message: string;
  confirmLabel: string;
};

export const areStringArraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((item, index) => item === b[index]);

export const normalizeStatusValue = (status?: string | null) => {
  const normalized = status?.toLowerCase?.().trim?.() || '';
  if (!normalized) return '';
  if (normalized === 'confirmado') return 'confirmada';
  if (normalized === 'cancelado' || normalized === 'cancelada') return 'cancelada';
  if (normalized === 'finalizado' || normalized === 'finalizada') return 'concluida';
  if (normalized === 'concluido' || normalized === 'concluída') return 'concluida';
  if (normalized === 'no show' || normalized === 'no-show' || normalized === 'noshow') return 'no_show';
  return normalized;
};

export const resolveDisplayStatus = (status: string, requiresConfirmation: boolean) => {
  if (!requiresConfirmation && !['cancelada', 'concluida', 'no_show'].includes(status)) {
    return 'confirmada';
  }
  return status;
};

export const getReservationStatusMeta = (status: string | null | undefined, requiresConfirmation: boolean) => {
  const normalizedStatus = normalizeStatusValue(status);
  const effectiveStatus = normalizedStatus || (requiresConfirmation ? 'pendente' : 'confirmada');
  const displayStatus = resolveDisplayStatus(effectiveStatus, requiresConfirmation);

  return {
    normalizedStatus,
    effectiveStatus,
    displayStatus,
  };
};

export const getTimeSortValue = (time?: string | null): number => {
  if (!time) return Number.MAX_SAFE_INTEGER;
  const [hoursRaw, minutesRaw] = String(time).split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return Number.MAX_SAFE_INTEGER;
  return (hours * 60) + minutes;
};

export const normalizeDate = (date: Date) => (
  new Date(date.getFullYear(), date.getMonth(), date.getDate())
);

export const formatDateKey = (date: Date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

export const shiftDate = (date: Date, amount: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + amount);
  return normalizeDate(nextDate);
};

export const formatDashboardDateLabel = (date: Date) => (
  date.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
);

export const sanitizeFileSegment = (value: string) => {
  const sanitized = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return sanitized || 'restaurante';
};

export const buildQrCodeFileName = (type: 'reservas' | 'fila', slug: string) => (
  `qr-${type}-${sanitizeFileSegment(slug)}.png`
);

export const sortReservationsByTime = (rows: any[] = []) => {
  return [...rows].sort((a, b) => {
    const aTime = getTimeSortValue(a?.time);
    const bTime = getTimeSortValue(b?.time);
    if (aTime !== bTime) return aTime - bTime;
    return String(a?.id || '').localeCompare(String(b?.id || ''));
  });
};

export const buildReservedTableMeta = (
  reservationsList: any[] = [],
  selectedReservationId: string | null = null,
) => {
  const meta = new Map<string, ReservedTableMeta>();

  (Array.isArray(reservationsList) ? reservationsList : []).forEach((reservation) => {
    extractReservationTableIds(reservation).forEach((tableId) => {
      const current = meta.get(tableId) || { count: 0, hasSelectedReservation: false };
      current.count += 1;
      if (
        selectedReservationId
        && String(reservation.id) === String(selectedReservationId)
      ) {
        current.hasSelectedReservation = true;
      }
      meta.set(tableId, current);
    });
  });

  return meta;
};

export const getTableCapacityRange = (table: any) => {
  const min = getPeopleCount(table?.minCovers ?? table?.min) || 1;
  const max = Math.max(min, getPeopleCount(table?.maxCovers ?? table?.max) || min);
  return { min, max };
};

export const formatTableCapacityRange = (table: any) => {
  const { min, max } = getTableCapacityRange(table);
  if (min === max) {
    return formatPeopleLabel(max);
  }
  return `${min} a ${formatPeopleLabel(max)}`;
};

export const getTableCapacityGroupKey = (table: any) => {
  const { min, max } = getTableCapacityRange(table);
  return `${min}-${max}`;
};

export const calculateCombinedTableCapacity = (tables: any[] = []) => {
  const normalizedTables = (Array.isArray(tables) ? tables : []).filter(Boolean);
  if (normalizedTables.length === 0) return null;

  const maxCapacities = normalizedTables.map((table) => getTableCapacityRange(table).max);
  const totalMaxCapacity = maxCapacities.reduce((total, capacity) => total + capacity, 0);

  if (normalizedTables.length === 1) {
    return totalMaxCapacity;
  }

  // Mantém uma folga operacional de 2 lugares para cada mesa adicional da composição.
  const adjustedCapacity = totalMaxCapacity - (Math.max(0, normalizedTables.length - 1) * 2);
  const highestSingleCapacity = Math.max(...maxCapacities);
  return Math.max(highestSingleCapacity, adjustedCapacity);
};

export const buildBelowMinimumCapacityWarning = (
  guestCount: number,
  minimum: number,
  subjectLabel: string
): CapacityWarningInfo => ({
  title: 'Grupo abaixo do mínimo configurado',
  message: `O grupo tem ${formatPeopleLabel(guestCount)} e ${subjectLabel} trabalha a partir de ${formatPeopleLabel(minimum)}.`,
  confirmLabel: 'Estou de acordo em seguir com a quantidade abaixo do mínimo configurado.',
});

export const getTableCapacityLabel = (table: any) => {
  return `Capacidade: ${formatTableCapacityRange(table)}`;
};

export const isAwaitingGuestOcupacao = (ocupacao?: MesaOcupacao | null) => {
  return Boolean(
    ocupacao
    && ocupacao.reserva_id !== null
    && ocupacao.reserva_id !== undefined
    && !ocupacao.seated_at
  );
};

export const getReleaseAllActionLabel = (ocupacoes: MesaOcupacao[] = []) => (
  ocupacoes.length > 0 && ocupacoes.every((ocupacao) => isAwaitingGuestOcupacao(ocupacao))
    ? 'Liberar todas'
    : 'Levantar todas'
);

export const buildTableTooltip = (
  table: any,
  ocupacao?: MesaOcupacao | null,
  reservation?: any | null,
) => {
  const lines = [getTableCapacityLabel(table)];

  if (!ocupacao) {
    return lines.join('\n');
  }

  const walkInName = String(ocupacao?.nome_cliente || '').trim();
  if (reservation?.name || walkInName) {
    lines.push(`Cliente: ${reservation?.name || walkInName}`);
  }

  const reservationGuests = getPeopleCount(reservation?.guests);
  const occupiedGuests = getPeopleCount(ocupacao?.numero_pessoas);

  if (isAwaitingGuestOcupacao(ocupacao)) {
    lines.push('Situação: aguardando cliente');
    if (reservationGuests) {
      lines.push(`Reserva: ${formatPeopleLabel(reservationGuests)}`);
    } else if (occupiedGuests) {
      lines.push(`Reserva vinculada: ${formatPeopleLabel(occupiedGuests)}`);
    }
    return lines.join('\n');
  }

  if (reservationGuests) {
    lines.push(`Reserva: ${formatPeopleLabel(reservationGuests)}`);
  } else if (occupiedGuests) {
    lines.push(`Mesa ocupada: ${formatPeopleLabel(occupiedGuests)}`);
  } else {
    lines.push('Mesa ocupada');
  }

  return lines.join('\n');
};
